import { getExtensionsFromMimeTypes } from "@/lib/balance-bill-utils";
import { getOS } from "@/lib/os-detector";
import type { ChangeEvent, ReactNode } from "react";
import React, { useRef } from "react";

type ImagePickerProps = {
  onImageSelect?: (files: File[]) => void;
  onValidationError?: ({
    message,
    subMessage,
  }: {
    message: string;
    subMessage: string;
  }) => void;
  trigger: ReactNode;
  className?: string;
  maxFiles?: number;
  allowedTypes?: string[]; // e.g., ["image/jpeg", "image/png"]
};

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const ImagePicker: React.FC<ImagePickerProps> = ({
  onImageSelect,
  onValidationError,
  trigger,
  className = "",
  maxFiles = 1,
  allowedTypes = ["image/*"],
}) => {
  const os = getOS();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Resets the file input before opening the file picker. This ensures that selecting the same file again will still trigger the onChange event.
    }
    fileInputRef.current?.click();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    // Check maxFiles
    if (files.length > maxFiles) {
      onValidationError?.({
        message: "Too Many Files Selected",
        subMessage: `Maximum ${maxFiles} files are allowed. Please select upto ${maxFiles} files.`,
      });
      // Clear input selection so user can re-select
      e.target.value = "";
      return;
    }

    // OS-specific HEIC/TIFF validation (pre-check)
    const hasBlockedHeic = Array.from(files).some((file) => {
      const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;

      if (fileExtension === ".tiff") {
        onValidationError?.({
          message: "TIFF files are not supported on this device",
          subMessage:
            "Please convert the TIFF file to JPG or PNG and try again.",
        });
        return true;
      }

      if (
        fileExtension === ".heic" &&
        (os.isWindows || os.isLinux || os.isAndroid)
      ) {
        onValidationError?.({
          message: "HEIC files are not supported on this device",
          subMessage:
            "Please convert the HEIC file to JPG or PNG and try again.",
        });
        return true;
      }
      return false;
    });

    if (hasBlockedHeic) {
      e.target.value = "";
      return;
    }

    // Check file types
    const invalidFiles = Array.from(files).filter((file) => {
      const fileType = file.type;
      const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;

      const isMimeTypeAllowed = fileType && allowedTypes?.includes(fileType);

      const isExtensionAllowed = allowedTypes?.includes(fileExtension);

      return !isMimeTypeAllowed && !isExtensionAllowed;
    });

    if (invalidFiles.length > 0) {
      const acceptedTypes = getExtensionsFromMimeTypes(allowedTypes);
      onValidationError?.({
        message: "Oops! Incorrect file types.",
        subMessage: `Only ${acceptedTypes.join(", ")} file types are accepted.`,
      });
      e.target.value = "";
      return;
    }

    //  Check file size
    const oversizedFiles = Array?.from(files)?.filter(
      (file) => file?.size > MAX_FILE_SIZE_BYTES,
    );
    if (oversizedFiles?.length > 0) {
      onValidationError?.({
        message: "Oops! File Too Large",
        subMessage: `File exceeds ${MAX_FILE_SIZE_MB}MB. Please select a smaller file to continue.`,
      });
      e.target.value = "";
      return;
    }

    const validFiles = Array?.from(files)?.slice(0, maxFiles);
    onImageSelect?.(validFiles);
  };

  const acceptAttr = allowedTypes.join(",");

  return (
    <div className={`${className}`}>
      {/* 2.1.1 Keyboard — A / 4.1.2 — A: the trigger that opens the native file picker
          was a <div onClick>, so it could not be reached or activated by keyboard. */}
      <button
        type="button"
        onClick={handleClick}
        className="w-full cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {trigger}
      </button>
      <input
        type="file"
        accept={acceptAttr}
        ref={fileInputRef}
        className="hidden"
        onChange={handleChange}
        multiple={maxFiles > 1}
      />
    </div>
  );
};

export default ImagePicker;
